
import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'What exactly does EasyMarketPro do?',
      answer:
        'We design and implement AI customer support agents for SaaS companies. Our agents handle repetitive and high-volume support tickets, retrieve information from your internal systems, and escalate complex cases to human agents.',
    },
    {
      question: 'How does EasyMarketPro integrate with my current support workflows?',
      answer:
        'Our AI agents are built to work inside your existing support infrastructure. We identify high-volume ticket categories, define automation boundaries, and integrate without disrupting your ongoing operations.',
    },
    {
      question: 'Do I need to hire more staff to use AI agents?',
      answer:
        'No. Our AI agents are designed to reduce ticket volume and support costs without increasing headcount. They handle repetitive tasks so your human agents can focus on complex issues.',
    },
    {
      question: 'How long does it take to set up an AI agent?',
      answer:
        'Setup times vary depending on your workflows, but most companies see initial implementation in a few weeks, including configuration, testing, and training of the AI.',
    },
    {
      question: 'Will AI replace my support team?',
      answer:
        'No. AI acts as a multiplier for your existing support team, not a replacement. It improves efficiency, consistency, and response times while your staff handles higher-value interactions.',
    },
    {
      question: 'What kind of ROI can I expect?',
      answer:
        'Companies using our AI agents typically see reduced ticket volume, faster response times, and lower support costs, providing measurable ROI within the first few months.',
    },
    {
      question: 'What if my support tickets are unique or complex?',
      answer:
        'Our system is designed to escalate edge cases to human agents automatically. AI only handles repetitive and predictable tasks, so unique or complex tickets are never mishandled.',
    },
    {
      question: 'Do I need technical expertise to use EasyMarketPro?',
      answer:
        'No. We handle the design, implementation, and integration of the AI agent. You only need to provide access to your support tools and workflows.',
    },
    {
      question: 'Can your AI agents learn and improve over time?',
      answer:
        'Yes. They continuously learn from interactions, helping your support system become more efficient and accurate as it handles recurring queries.',
    },
    {
      question: 'How do I get started?',
      // Use double quotes to avoid breaking the string with an apostrophe
      answer:
        "Reach out to us via our contact form or email, and we'll schedule a consultation to understand your workflows and define a custom AI support plan.",
    },
  ];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="bg-white py-16 md:py-28">
      <div className="max-w-[900px] mx-auto px-5 md:px-8">
        <div
          ref={headerRef}
          className="text-center mb-10 md:mb-14"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(32px)',
            transition:
              'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#B8985F]/10 mb-5 md:mb-6">
            <span className="text-[12px] tracking-[1.5px] uppercase text-[#B8985F] font-semibold">
              FAQ
            </span>
          </div>
          <h2 className="text-[28px] md:text-[38px] font-bold text-[#1A1A1A] mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-[14px] md:text-[16px] text-[#777] max-w-[600px] mx-auto">
            Everything you need to know about AI customer support agents and how
            they work for your business.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-[#F9F8F6] rounded-lg border border-[#F0EDE6] overflow-hidden transition-all duration-300 hover:border-[#B8985F]/30"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-5 md:px-7 py-4 md:py-5 flex items-center justify-between gap-4 text-left cursor-pointer group"
                aria-expanded={openIndex === index}
              >
                <span className="text-[14px] md:text-[15px] font-semibold text-[#1A1A1A] group-hover:text-[#B8985F] transition-colors">
                  {faq.question}
                </span>
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  <i
                    className={`${
                      openIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                    } text-[#B8985F] text-lg transition-transform duration-300`}
                    style={{
                      transform:
                        openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  ></i>
                </div>
              </button>
              <div
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                  maxHeight: openIndex === index ? '500px' : '0px',
                  opacity: openIndex === index ? 1 : 0,
                }}
              >
                <div className="px-5 md:px-7 pb-4 md:pb-5">
                  <p className="text-[13px] md:text-[14px] text-[#666] leading-[1.7]">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
